import { Button, Modal } from 'antd';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { BsWhatsapp } from 'react-icons/bs';
import {
  HiOutlineExclamation,
  HiOutlineExclamationCircle,
  HiOutlineLockClosed,
} from 'react-icons/hi';

import { useAuth } from '../../../hooks/useAuth';
import {
  acknowledgeDefaulterNotice,
  getDefaulterNotice,
} from '../../../operations/defaulterNotice';
import { DefaulterNotice } from '../../../operations/defaulterNotice/types';
import {
  AckBody,
  AckIcon,
  AckTitle,
  BannerStyled,
  BannerText,
  BlockedCard,
  BlockedContent,
  BlockedIcon,
  BlockedLayer,
  BlockedText,
  BlockedTitle,
  WhatsappButton,
} from './styles';

/*
 * Aviso de inadimplência no painel (CON-328). O nível vem do backend:
 * 1 banner; 2 aviso que só fecha com "Li e estou ciente"; 3 painel
 * bloqueado, com o conteúdo visível por trás e sem clique.
 */

const whatsappLink = (phone: string, customerName?: string) => {
  const text = customerName
    ? `Olá! Sou da ${customerName} e quero falar sobre as mensalidades em aberto.`
    : 'Olá! Quero falar sobre as mensalidades em aberto.';
  return `https://wa.me/55${phone}?text=${encodeURIComponent(text)}`;
};

const FinanceWhatsapp = ({
  phone,
  customerName,
  label = 'Falar com o financeiro',
}: {
  phone: string | null;
  customerName?: string;
  label?: string;
}) =>
  phone ? (
    <WhatsappButton
      href={whatsappLink(phone, customerName)}
      target="_blank"
      rel="noopener noreferrer"
    >
      <BsWhatsapp />
      {label}
    </WhatsappButton>
  ) : null;

export const useDefaulterNotice = () => {
  const { user } = useAuth();
  const [notice, setNotice] = useState<DefaulterNotice | null>(null);
  const customerKey = user && user.id ? `${user.id}:${user.customer_name}` : '';

  useEffect(() => {
    let active = true;
    if (!customerKey) return undefined;

    getDefaulterNotice()
      .then((data) => {
        if (active) setNotice(data);
      })
      .catch(() => {
        // Sem aviso se a consulta falhar: o painel segue normal
        if (active) setNotice(null);
      });

    return () => {
      active = false;
    };
  }, [customerKey]);

  return { notice, setNotice, customerName: user?.customer_name };
};

export const DefaulterBanner = ({
  notice,
  customerName,
}: {
  notice: DefaulterNotice | null;
  customerName?: string;
}) => {
  const [closed, setClosed] = useState(false);

  if (!notice || closed || notice.blocked || notice.ack_required) return null;
  if (notice.level !== 1 && notice.level !== 2) return null;

  return (
    <BannerStyled $level={notice.level} role="alert">
      <HiOutlineExclamationCircle />
      <BannerText>{notice.message}</BannerText>
      <FinanceWhatsapp phone={notice.whatsapp} customerName={customerName} />
      <Button type="text" size="small" onClick={() => setClosed(true)}>
        Fechar
      </Button>
    </BannerStyled>
  );
};

export const DefaulterAckModal = ({
  notice,
  customerName,
  onAcknowledged,
}: {
  notice: DefaulterNotice | null;
  customerName?: string;
  onAcknowledged: () => void;
}) => {
  const [saving, setSaving] = useState(false);

  const handleAck = useCallback(async () => {
    setSaving(true);
    try {
      await acknowledgeDefaulterNotice();
      onAcknowledged();
    } finally {
      setSaving(false);
    }
  }, [onAcknowledged]);

  const visible = !!notice && notice.level === 2 && notice.ack_required;

  return (
    <Modal
      visible={visible}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      width={480}
      footer={null}
    >
      <AckBody>
        <AckIcon>
          <HiOutlineExclamation />
        </AckIcon>
        <AckTitle>Mensalidades em aberto</AckTitle>
        <p>{notice ? notice.message : ''}</p>
        <Button
          type="primary"
          size="large"
          block
          loading={saving}
          onClick={handleAck}
          style={{ background: 'var(--primary)', borderColor: 'var(--primary)' }}
        >
          Li e estou ciente
        </Button>
        {notice && (
          <FinanceWhatsapp
            phone={notice.whatsapp}
            customerName={customerName}
            label="Falar com o financeiro"
          />
        )}
      </AckBody>
    </Modal>
  );
};

export const DefaulterBlock = ({
  notice,
  customerName,
  children,
}: {
  notice: DefaulterNotice | null;
  customerName?: string;
  children: ReactNode;
}) => {
  if (!notice || !notice.blocked) return <>{children}</>;

  return (
    <BlockedLayer>
      <BlockedContent aria-hidden="true">{children}</BlockedContent>
      <BlockedCard role="alertdialog" aria-labelledby="defaulter-block-title">
        <BlockedIcon>
          <HiOutlineLockClosed />
        </BlockedIcon>
        <BlockedTitle id="defaulter-block-title">Acesso suspenso</BlockedTitle>
        <BlockedText>{notice.message}</BlockedText>
        <FinanceWhatsapp phone={notice.whatsapp} customerName={customerName} />
        {!notice.whatsapp && (
          <BlockedText>Entre em contato com o escritório para liberar.</BlockedText>
        )}
      </BlockedCard>
    </BlockedLayer>
  );
};
